"use client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState } from "react";
import LyricsReportsTable from "~/components/lyrics/AdminLyricsReportsTable";
import { adminLyricsReportsService } from "~/connectors/lyricsReports.connector";
import type { LyricsReport, LyricsWithSlug } from "~/types";

interface Props {
  lyrics: LyricsWithSlug;
}

export default function AdminLyricsReports({ lyrics }: Props) {
  const slug = lyrics.slug;

  const [reports, setReports] = useState<LyricsReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [fetched, setFetched] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminLyricsReportsService.getReports({ slug, page: 1, limit: 100 });
      setReports(result.data);
    } catch {
      // silently ignore errors
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [slug]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const total = reports.length;
  const unresolved = reports.filter((r) => r.status !== "resolved").length;

  const hasReports = total > 0;

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {loading && !fetched ? (
            <CircularProgress size={16} />
          ) : (
            hasReports && (
              <>
                <Chip
                  icon={<ReportProblemIcon color="warning" />}
                  label={
                    <Typography variant="body2" color="textSecondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.875rem" }}>
                      {unresolved}/{total} probleme nerezolvate

                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Typography>
                  }
                  size="small"
                  variant="outlined"
                  onClick={handleToggle}
                  sx={{ cursor: "pointer", pl: .5, ml: 1 }}
                />
              </>
            )
          )}
        </Box>
      </Box>

      {hasReports && (
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <LyricsReportsTable reports={reports} onReportsChange={fetchReports} />
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
